import {parseUnifiedDiff} from '@github-ui/agent-sessions/utils/parse-unified-diff'
import {RuleSeverityBadge} from '@github-ui/code-scanning-shared/components/RuleSeverityBadge'
import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
import {AgentIcon, ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {clsx} from 'clsx'
import {type FC, useMemo, useState} from 'react'
import {getFindingFileLocation, getFindingSeverity, getFindingSource, getFindingTitle} from './entity-helpers'
import styles from './Finding.module.css'
import type {Finding as FindingType} from './types'

interface FindingProps {
  finding: FindingType
  resolved: boolean
  onDismiss: () => void
  onReopen: () => void
  onFix: () => void
}

export const Finding: FC<FindingProps> = ({finding, resolved, onDismiss, onReopen, onFix}) => {
  const [showDetails, setShowDetails] = useState(false)
  const severity = getFindingSeverity(finding)
  const source = getFindingSource(finding)
  const fileLocation = getFindingFileLocation(finding)
  const title = getFindingTitle(finding)

  const diffLines = useMemo(
    () => (finding.suggestion ? parseUnifiedDiff(finding.suggestion) : []),
    [finding.suggestion],
  )

  return (
    <li className={styles.Finding}>
      <button
        type="button"
        className={styles.header}
        onClick={() => setShowDetails(prev => !prev)}
        aria-expanded={showDetails}
      >
        <span className={styles.chevron}>
          {showDetails ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
        </span>
        <span className={styles.headerContent}>
          <span className={styles.title}>{title}</span>
          <span className={styles.meta}>
            {severity && <RuleSeverityBadge severity={severity} />}
            {source && (
              <>
                <span className={styles.metaSeparator}>{'\u00B7'}</span>
                <span>{source}</span>
              </>
            )}
            {fileLocation && (
              <>
                <span className={styles.metaSeparator}>{'\u00B7'}</span>
                <span className={styles.fileLocation}>{fileLocation}</span>
              </>
            )}
          </span>
        </span>
      </button>
      {showDetails && (
        <div className={styles.expandedContent}>
          <div className={styles.section}>
            <p>{finding.message}</p>
          </div>
          {diffLines.length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Suggestions</h3>
              <div className={styles.diffPreview}>
                <div className={clsx('d-flex flex-column overflow-hidden', styles.diffEntryHeader)}>
                  <div className="d-flex flex-items-center tmp-px-3 py-2 text-small gap-2 text-mono">
                    {finding.filePath}
                  </div>
                  <StaticUnifiedDiffPreview
                    tabSize={4}
                    className={clsx(
                      'm-0 border-bottom borderColor-default overflow-hidden',
                      styles.automatedReviewDiff,
                    )}
                    diffTableClassName="borderColor-default"
                    diffLines={diffLines}
                  />
                </div>
              </div>
            </div>
          )}
          {!diffLines.length && finding.suggestion && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Suggestion</h3>
              <pre className="color-bg-subtle tmp-p-3 rounded-2 overflow-auto text-small">{finding.suggestion}</pre>
            </div>
          )}
          <div className={styles.footer}>
            <div className="d-flex gap-2">
              {!resolved && (
                <Button leadingVisual={AgentIcon} onClick={onFix}>
                  Fix with Copilot
                </Button>
              )}
              {resolved ? <Button onClick={onReopen}>Reopen</Button> : <Button onClick={onDismiss}>Dismiss</Button>}
            </div>
          </div>
        </div>
      )}
    </li>
  )
}
